import { useEffect, useRef } from "react";

interface PaymentEventDetail {
  data: { resultCode: string; refusalReason?: string; [key: string]: unknown };
  element?: unknown;
}

interface PaymentErrorDetail {
  error: { name: string; message: string };
  element?: unknown;
}

interface StorePaymentMethodChangeDetail {
  storePaymentMethod: boolean;
}

interface PaymentServiceEventsOptions {
  onPaymentCompleted: (e: CustomEvent<PaymentEventDetail>) => void;
  onError: (e: CustomEvent<PaymentErrorDetail>) => void;
}

export const emitPaymentServiceEvent = (type: string, detail: unknown) => {
  const event = new CustomEvent(type, { detail });
  window.dispatchEvent(event);
};

export const usePaymentServiceEvents = (opts: PaymentServiceEventsOptions) => {
  const callbacksRef = useRef(opts);
  useEffect(() => {
    callbacksRef.current = opts;
  });

  useEffect(() => {
    const onPaymentCompleted = (e: Event) =>
      callbacksRef.current.onPaymentCompleted(e as CustomEvent<PaymentEventDetail>);
    const onError = (e: Event) =>
      callbacksRef.current.onError(e as CustomEvent<PaymentErrorDetail>);

    window.addEventListener("onPaymentCompleted", onPaymentCompleted);
    window.addEventListener("onError", onError);

    return () => {
      window.removeEventListener("onPaymentCompleted", onPaymentCompleted);
      window.removeEventListener("onError", onError);
    };
  }, []);
};

/**
 * Subscribe to Adyen's "Save for my next payment" checkbox state changes.
 * Dispatched by the card component's onChange callback in paymentOptions.tsx.
 */
export const useStorePaymentMethodChange = (
  onChange: (storePaymentMethod: boolean) => void,
) => {
  const callbackRef = useRef(onChange);
  useEffect(() => {
    callbackRef.current = onChange;
  });

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<StorePaymentMethodChangeDetail>).detail;
      callbackRef.current(!!detail?.storePaymentMethod);
    };
    window.addEventListener("onStorePaymentMethodChange", handler);
    return () => {
      window.removeEventListener("onStorePaymentMethodChange", handler);
    };
  }, []);
};
